import { inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { BehaviorSubject } from 'rxjs';
import { tap, catchError } from 'rxjs/operators';
import { of } from 'rxjs';
import { DialogService } from 'primeng/dynamicdialog';
import { AppConfigService } from '../config/app-config.service';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private appConfig = inject(AppConfigService);

  private get apiUrl(): string {
    return `${this.appConfig.backendUrl}/api/v1/auth`;
  }
  private accessToken = 'accessToken'; // Store access token in localStorage
  private refreshToken = 'refreshToken'; // Store refresh token in localStorage

  // To track the user's authentication state
  isAuthenticated = new BehaviorSubject<boolean>(false);

  constructor(
    private http: HttpClient,
    private router: Router,
    private dialogService: DialogService
  ) {}

  // Login method
  login(credentials: { usernameOrEmail: string; password: string }) {
    return this.http
      .post<{ accessToken: string; refreshToken: string }>(
        `${this.apiUrl}/login`,
        credentials
      )
      .pipe(
        tap((tokens) => {
          this.storeTokens(tokens);
          this.isAuthenticated.next(true);
          this.router.navigate(['/dashboard']); // Redirect on success
        })
      );
  }

  register(registerData: any, options?: { autoNavigate?: boolean }) {
    const autoNavigate = options?.autoNavigate ?? true;
    return this.http
      .post<{ accessToken: string; refreshToken: string }>(
        `${this.apiUrl}/register`,
        registerData
      )
      .pipe(
        tap((tokens) => {
          this.storeTokens(tokens);
          this.isAuthenticated.next(true);
          if (autoNavigate) {
            this.router.navigate(['/dashboard']);
          }
        })
      );
  }

  getValidBureauUICs() {
    return this.http.get<string[]>(`${this.appConfig.backendUrl}/api/v1/bureauUICs`);
  }

  // Store tokens
  private storeTokens(tokens: { accessToken: string; refreshToken: string }) {
    localStorage.setItem(this.accessToken, tokens.accessToken); // Store access token in localStorage
    localStorage.setItem(this.refreshToken, tokens.refreshToken); // Store refresh token in localStorage
  }

  // Retrieve the access token
  getAccessToken() {
    return localStorage.getItem(this.accessToken);
  }

  // Retrieve the refresh token
  getRefreshToken() {
    return localStorage.getItem(this.refreshToken);
  }

  private closeAllOpenDialogs(): void {
    try {
      const refs = Array.from(this.dialogService.dialogComponentRefMap?.keys() ?? []);
      refs.forEach((ref) => ref.close(null));
    } catch {
    }
  }

  // Clear tokens on logout
  logout() {
    const refreshToken = this.getRefreshToken();
    return this.http.post(`${this.apiUrl}/logout`, { refreshToken }).pipe(
      tap(() => this.doLogoutCleanup()),
      catchError(() => {
        this.doLogoutCleanup();
        return of(null);
      })
    );
  }

  private doLogoutCleanup(): void {
    this.closeAllOpenDialogs();
    this.clearTokens();
    this.isAuthenticated.next(false);
    this.router.navigate(['/login']);
  }

  private clearTokens() {
    localStorage.removeItem(this.accessToken); // Clear access token from localStorage
    localStorage.removeItem(this.refreshToken); // Clear refresh token from localStorage
  }

  // Refresh the access token using the refresh token
  refreshTokens() {
    const refreshToken = this.getRefreshToken();
    if (!refreshToken) {
      throw new Error('Refresh token is missing');
    }

    return this.http
      .post<{ refreshToken: string; accessToken: string }>
      (`${this.apiUrl}/refreshTokens`, { refreshToken })
      .pipe(
        tap((response) => {
          localStorage.setItem(this.accessToken, response.accessToken); // Update access token in localStorage
          localStorage.setItem(this.refreshToken, response.refreshToken); // Update refresh token in localStorage
        })
      );
  }
}
